import { useRef, useEffect, useState, useCallback } from "react";
import { Eraser, X, Check, Hand } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

/**
 * SignaturePad — touch & mouse signature canvas.
 * Returns a PNG dataURL via onConfirm(dataUrl).
 */
export default function SignaturePad({
  onConfirm,
  onCancel,
  label = "Teken hier met je vinger of pen",
  width = 600,
  height = 220,
  strokeColor = "#0F172A",
}) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const lastPos = useRef(null);
  const [size, setSize] = useState({ w: width, h: height });

  // Responsive: schaal canvas naar parent breedte
  useEffect(() => {
    const update = () => {
      const parent = canvasRef.current?.parentElement;
      if (!parent) return;
      const w = Math.min(parent.clientWidth - 4, 800);
      const h = Math.max(180, Math.min(280, w * 0.36));
      setSize({ w, h });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Init canvas drawing context
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    // Hi-DPI
    const dpr = window.devicePixelRatio || 1;
    c.width = size.w * dpr;
    c.height = size.h * dpr;
    c.style.width = `${size.w}px`;
    c.style.height = `${size.h}px`;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.5;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = strokeColor;
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, size.w, size.h);
  }, [size, strokeColor]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    lastPos.current = getPos(e);
    setIsEmpty(false);
  };
  const move = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const p = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPos.current = p;
  };
  const end = (e) => {
    e.preventDefault();
    setIsDrawing(false);
    lastPos.current = null;
  };

  const clear = useCallback(() => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, size.w, size.h);
    setIsEmpty(true);
  }, [size]);

  const confirm = () => {
    if (isEmpty) return;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onConfirm?.(dataUrl);
  };

  return (
    <div className="space-y-2 w-full" data-testid="signature-pad">
      {label && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Hand size={12} weight="bold" /> {label}
        </p>
      )}
      <div className="border-2 border-dashed border-[#002FA7] rounded-sm bg-white relative overflow-hidden" style={{ width: size.w, height: size.h }}>
        <canvas
          ref={canvasRef}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          className="cursor-crosshair touch-none block"
          data-testid="signature-canvas"
        />
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-muted-foreground text-xs italic">— teken hier —</span>
          </div>
        )}
      </div>
      <div className="flex justify-between gap-2">
        <Button variant="outline" size="sm" onClick={clear} disabled={isEmpty} data-testid="sig-clear">
          <Eraser size={14} weight="bold" className="mr-1" /> Wissen
        </Button>
        <div className="flex gap-2">
          {onCancel && (
            <Button variant="outline" size="sm" onClick={onCancel} data-testid="sig-cancel">
              <X size={14} weight="bold" className="mr-1" /> Annuleren
            </Button>
          )}
          <Button
            size="sm"
            onClick={confirm}
            disabled={isEmpty}
            className="bg-[#002FA7] hover:bg-[#002280] text-white"
            data-testid="sig-confirm"
          >
            <Check size={14} weight="bold" className="mr-1" /> Bevestigen
          </Button>
        </div>
      </div>
    </div>
  );
}
